import React, {
  useEffect,
  useState,
  useContext,
  ChangeEvent,
  useRef,
} from "react";

import { Box, Container, Grid, Typography } from "@material-ui/core";
import { AuthContext } from "../auth/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ButtonLoader from "../components/ButtonLoader";
import { emailValid, passwordValid, phoneNumberValid } from "../lib/validation";
import useScript from "../hooks/useScript";
import { Environment } from "../utils/environments";
import { httpPost } from "../lib/apiClients";
import SectionHeader from "../components/SectionHeader";
import { BiShowAlt, BiHide } from "react-icons/bi";
import { FaCheck } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { PiCopyDuotone } from "react-icons/pi";
import { BsChevronDown } from "react-icons/bs";
function Signup() {
  const navigate = useNavigate();
  const emailDomainsDefault = [
    "gmail.com",
    "yahoo.co.jp",
    "icloud.com",
    "outlook.jp",
    "ezweb.ne.jp",
    "docomo.ne.jp",
    "i.softbank.jp",
    "nifty.com",
    "jcom.home.ne.jp",
    "hotmail.co.jp",
    "au.com",
    "hi3.enjoy.ne.jp",
    "outlook.com",
    "ymail.ne.jp",
    "uqmobile.jp",
    "live.jp",
    "hotmail.com",
    "rakuten.jp",
    "ymobile.ne.jp",
    "nifty.com",
    "mineo.jp",
  ];
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean | string>(false);
  const [firstNameError, setFirstNameError] = useState<boolean | string>(false);
  const [emailError, setEmailError] = useState<boolean | string>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean | string>(
    false
  );
  const [emailEntered, setEmailEntered] = useState<string | undefined>(
    undefined
  );
  const [passwordError, setPasswordError] = useState<boolean | string>(false);
  const [signUpError, setSignUpError] = useState<boolean | string>(false);
  const [showPassword, setShowPassword] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [passwordMatch, setPasswordMatch] = useState<boolean>(false);
  const [emailRegexError, setEmailRegexError] = useState<boolean>(false);
  const [passwordRegexError, setPasswordRegexError] = useState<boolean>(false);
  const [showDomainDropdown, setShowDomainDropDown] = useState<boolean>(false);
  const [emailDomains, setEmailDomains] =
    useState<string[]>(emailDomainsDefault);
  const dropdownRef = useRef<HTMLFormElement>(null);
  const [copyText, setCopyText] = useState<string>("パスワードをコピー");
  const displayLp = searchParams.get("display")
    ? searchParams.get("display")
    : undefined;
  const lpSource = searchParams.get("lpSource") ?? "";
  var pageHeight = 0;
  const siteKey = Environment.getRecaptchaKey();
  if (displayLp != "lp") {
    const loadrecaptcha = useScript(
      `https://www.google.com/recaptcha/api.js?render=${siteKey}`,
      {
        removeOnUnmount: true,
      }
    );
  }
  // const utmSource = searchParams.get("utm_source")
  //   ? searchParams.get("utm_source")
  //   : "";
  // const utmMedium = searchParams.get("utm_medium")
  //   ? searchParams.get("utm_medium")
  //   : "";
  // const utmCampaign = searchParams.get("utm_campaign")
  //   ? searchParams.get("utm_campaign")
  //   : "";
  // console.log(`${utmSource} ${utmMedium} ${utmCampaign}`);
  useEffect(() => {
    document.title = "会員登録 | じぶん専用IT相談室";
    if (currentUser && displayLp != "lp") {
      navigate({
        pathname: "/",
      });
    }
    return () => {
      var captchaLogo = document.querySelectorAll(".grecaptcha-badge");
      captchaLogo.forEach((logo) => logo.parentElement?.remove());
    };
  }, [currentUser]);
  var formHeight: any = document.querySelector(".signup-form");
  pageHeight = formHeight?.offsetHeight;
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);
    const lastName = formData.get("lastName") as string;
    const firstName = formData.get("firstName") as string;
    const phoneNumber = formData.get("phoneNumber") as string;
    let email1 = formData.get("mail1") as string;
    let email2 = formData.get("mail2") as string;
    const email = `${email1}@${email2}`;
    const password = formData.get("password") as string;
    const survey: string[] = Array.from(formData.entries())
      .filter(([name]) => name === "survey")
      .map(([, value]) => value as string);

    const errors = {
      lastName: !lastName.trim() && "姓を入力してください",
      firstName: !firstName.trim() && "名を入力してください",
      email:
        (!email || !emailValid(email)) &&
        (email.length > 319
          ? "メールアドレスは、319文字以内で入力してください"
          : "メールアドレス形式で入力してください"),
      phoneNumber:
        (!phoneNumber || !phoneNumberValid(phoneNumber)) &&
        "半角の数値10桁か11桁で入力してください",
      password: !password
        ? "パスワードを入力してください"
        : (!passwordValid(password) || password.length > 12) &&
          "半角英数字記号・6文字以上12文字以内で入力してください",
    };

    setLastNameError(errors.lastName || false);
    setFirstNameError(errors.firstName || false);
    setEmailError(errors.email || false);
    setPhoneNumberError(errors.phoneNumber || false);
    setPasswordError(errors.password || false);

    const isError =
      Object.values(errors).some(Boolean) ||
      passwordRegexError ||
      emailRegexError;

    if (isError) {
      setLoading(false);
      return;
    }

    const handlePost = async (token: string) => {
      if (Environment.getBuildType() !== "prod") {
        console.log(token);
      }
      await handleSignUpPost(
        email,
        password,
        lastName,
        firstName,
        phoneNumber,
        survey,
        token
      );
    };

    if (displayLp !== "lp") {
      // @ts-ignore
      window.grecaptcha.ready(async () => {
        // @ts-ignore
        const token = await window.grecaptcha.execute(siteKey, {
          action: "submit",
        });
        await handlePost(token);
      });
    } else {
      await handlePost("");
    }
  }

  async function handleSignUpPost(
    email: string,
    password: string,
    lastName: string,
    firstName: string,
    phoneNumber: string,
    survey: string[],
    token: string
  ) {
    try {
      const response = await httpPost(
        {
          pathName: "signup-request",
          bodyParams: {
            memberLastname: lastName,
            memberFirstname: firstName,
            phoneNumber,
            email,
            password,
            registrationAnswers: survey,
            source: displayLp !== "lp" ? "SIGNUP_FORM" : "SIGNUP_FORM_LP",
            recaptchaToken: token,
            lpSource,
            chatbotSourceUrl: window.location.href,
          },
        },
        false
      );

      if (response.status === 200) {
        navigate(`/registeration-temp${window.location.search}`);
      }
    } catch (error: any) {
      if (Environment.getBuildType() !== "prod") {
        console.error(error);
      }

      if (error.response?.status >= 400 && error.response?.status < 500) {
        setSignUpError(true);
      } else {
        navigate("/error?code=400");
      }
    } finally {
      setLoading(false);
    }
  }

  const css = `
  .grecaptcha-badge {
    visibility: hidden;
  }
  `;
  const passwordGenerator = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const generatePassword = () => {
    const password = passwordGenerator();
    const passwordInput: HTMLInputElement | null =
      document.querySelector("#password");
    if (passwordInput) {
      passwordInput.value = password;
      setPasswordError(false);
      setPasswordMatch(true);
    }
  };

  const copyPassword = async () => {
    const passwordInput: HTMLInputElement | null =
      document.querySelector("#password");
    if (passwordInput) {
      passwordInput.select();
      passwordInput.setSelectionRange(0, 99999);
      navigator.clipboard.writeText(passwordInput.value);
      setCopyText("パスワードをコピーしました");
      setTimeout(() => {
        setCopyText("パスワードをコピー");
      }, 2000);
    }
  };
  const checkPasswordCriteria = (e: ChangeEvent<HTMLInputElement>) => {
    let value = preventFullWidth(e);
    value = value.slice(0, 12);
    e.target.value = value;
    setPasswordMatch(value.length >= 6 && value.length <= 12);
  };

  const preventFullWidth = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    value = value.replace(/[一-龠ぁ-ゔァ-ヴー々〆〤ヶ]/g, "");
    value = value.replace(/[^\x20-\x7E]/g, "");
    value = value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
      String.fromCharCode(s.charCodeAt(0) - 0xfee0)
    );

    return value;
  };

  const validateEmail = () => {
    const emailField = document.querySelector<HTMLInputElement>("#mail");
    const emailDomainField =
      document.querySelector<HTMLInputElement>("#domain");

    const emailUser = emailField?.value || "";
    const emailDomain = emailDomainField?.value || "";

    const fullEmail = emailDomain ? `${emailUser}@${emailDomain}` : emailUser;

    setEmailEntered(emailValid(fullEmail) ? undefined : fullEmail);

    if (emailError) {
      const errorMessage =
        fullEmail.length > 319
          ? "メールアドレスは、319文字以内で入力してください"
          : "メールアドレス形式で入力してください";
      setEmailError(
        !fullEmail || !emailValid(fullEmail) ? errorMessage : false
      );
    }
  };

  const zenkakuToHankaku = (str: string) => {
    return str
      .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
        String.fromCharCode(s.charCodeAt(0) - 0xfee0)
      )
      .replace(/[^0-9]/g, "");
  };

  const toHalfWidth = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let convertedText = zenkakuToHankaku(value);
    convertedText = convertedText.slice(0, 11);
    e.target.value = convertedText;
    return convertedText;
  };

  const addEmailDomain = (emailDomain: string) => {
    const emailDomainField =
      document.querySelector<HTMLInputElement>("#domain");
    if (emailDomainField) {
      emailDomainField.value = emailDomain;
      setShowDomainDropDown(false);
      validateEmail();
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = preventFullWidth(e);
    const { name } = e.target;
    const emailDomainField =
      document.querySelector<HTMLInputElement>("#domain");
    if (name === "mail1" && value.includes("@")) {
      const [user, domain] = value.split("@");
      e.target.value = user;
      if (emailDomainField) {
        emailDomainField.value = domain || "";
        emailDomainField.focus();
      }
    }
    if (name === "mail2") {
      setShowDomainDropDown(true);
      if (value.includes("@")) {
        e.target.value = value.split("@")[1];
      } else {
        e.target.value = value;
        const filteredDomains = emailDomainsDefault.filter((domain) =>
          domain.includes(value)
        );
        setShowDomainDropDown(filteredDomains.length > 0);
        setEmailDomains(
          filteredDomains.length ? filteredDomains : emailDomainsDefault
        );
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let { value, name } = e.target;
    if (name === "mail1" && signUpError) {
      setSignUpError(false);
    }
    const validations: Record<
      string,
      {
        error: boolean | string;
        message: string;
        isValid: (value: string) => boolean;
      }
    > = {
      lastName: {
        error: lastNameError,
        message: "姓を入力してください",
        isValid: (value) => !!value.trim(),
      },
      firstName: {
        error: firstNameError,
        message: "名を入力してください",
        isValid: (value) => !!value.trim(),
      },
      email: {
        error: emailError,
        message:
          value.length > 319
            ? "メールアドレスは、319文字以内で入力してください"
            : "メールアドレス形式で入力してください",
        isValid: (value) => !!value && emailValid(value) && value.length <= 319,
      },
      phoneNumber: {
        error: phoneNumberError,
        message: "半角の数値10桁か11桁で入力してください",
        isValid: (value) => !!value && phoneNumberValid(value),
      },
      password: {
        error: passwordError,
        message: "半角英数字記号・6文字以上12文字以内で入力してください",
        isValid: (value) =>
          !!value && passwordValid(value) && value.length <= 12,
      },
    };

    if (validations[name]?.error) {
      const { message, isValid } = validations[name];
      const setError = (name: string, error: boolean | string) => {
        switch (name) {
          case "lastName":
            setLastNameError(error);
            break;
          case "firstName":
            setFirstNameError(error);
            break;
          case "email":
            setEmailError(error);
            break;
          case "phoneNumber":
            setPhoneNumberError(error);
            break;
          case "password":
            setPasswordError(error);
            break;
          default:
            break;
        }
      };
      setError(name, isValid(value) ? false : message);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDomainDropDown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);
  return (
    <Box
      className={
        displayLp != "lp"
          ? "section bg-dots signup-page"
          : "signup-lp signup-page"
      }
      component={"section"}
    >
      {displayLp == "lp" && <style>{css}</style>}
      <Container className="container">
        {displayLp != "lp" && (
          <SectionHeader currentPage={"会員登録"} preHeading={"Member"} />
        )}

        <Box className="signup-form" style={{ minHeight: `${pageHeight}px` }}>
          <form onSubmit={handleSubmit} ref={dropdownRef}>
            {displayLp != "lp" && (
              <Typography
                style={{
                  marginBottom: "40px",
                  color: "var(--text-color-2)",
                  fontWeight: "600",
                }}
              >
                ご登録内容を入力してください。
                <br />
                外部には公開しないので、ご安心ください。
              </Typography>
            )}

            <Grid
              container
              className="signup-group"
              spacing={2}
              alignItems="center"
            >
              <Grid item md={3} sm={3} xs={12}>
                <label>
                  氏名 <span className="required">必須</span>
                </label>
              </Grid>
              <Grid
                item
                md={9}
                sm={9}
                xs={12}
                container
                style={{ gap: "10px" }}
              >
                <Grid item sm={true} xs={true}>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="姓"
                    className="input-field"
                    maxLength={25}
                    onChange={(e) => handleChange(e)}
                  />
                  {lastNameError && (
                    <Typography className="error-message">
                      {lastNameError}
                    </Typography>
                  )}
                </Grid>
                <Grid item sm={true} xs={true}>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="名"
                    className="input-field"
                    maxLength={25}
                    onChange={(e) => handleChange(e)}
                  />
                  {firstNameError && (
                    <Typography className="error-message">
                      {firstNameError}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              className="signup-group"
              spacing={2}
              alignItems="center"
            >
              <Grid item md={3} sm={3} xs={12}>
                <label>
                  電話番号 <span className="required">必須</span>
                </label>
              </Grid>
              <Grid item md={9} sm={9} xs={12}>
                <input
                  type="tel"
                  name="phoneNumber"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="09012345678"
                  className="input-field"
                  onChange={(e) => {
                    toHalfWidth(e);
                    handleChange(e);
                  }}
                />
                {phoneNumberError && (
                  <Typography className="error-message">
                    {phoneNumberError}
                  </Typography>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              className="signup-group"
              spacing={2}
              alignItems="center"
            >
              <Grid item md={3} sm={3} xs={12}>
                <label>
                  メールアドレス <span className="required">必須</span>
                </label>
              </Grid>
              <Grid item md={9} sm={9} xs={12}>
                <div className="email-field">
                  <input
                    type="text"
                    name="mail1"
                    placeholder="メールアドレス"
                    className="input-field"
                    id="mail"
                    autoComplete="off"
                    onChange={(e) => {
                      handleEmailChange(e);
                      validateEmail();
                    }}
                    // onBlur={(e) => validateEmail(e)}
                  />
                  <div className="at-sign">@</div>
                  <div className="email-domain">
                    <input
                      type="text"
                      name="mail2"
                      id="domain"
                      autoComplete="off"
                      className="input-field"
                      placeholder="メールアドレス"
                      onChange={(e) => {
                        handleEmailChange(e);
                        validateEmail();
                      }}
                      onFocus={() => setShowDomainDropDown(true)}
                    />
                    <BsChevronDown
                      onClick={() => setShowDomainDropDown(!showDomainDropdown)}
                    />
                    {showDomainDropdown && (
                      <div className="domain-dropdown">
                        {emailDomains.map((emailDomain, index) => (
                          <span
                            key={index}
                            onClick={() => addEmailDomain(emailDomain)}
                          >
                            {emailDomain}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {emailError && (
                    <Typography className="error-message">
                      {emailError}
                    </Typography>
                  )}
                  {emailEntered && (
                    <div className="email-error">
                      <span>
                        {emailRegexError
                          ? "半角英数字で入力お願いします"
                          : "お間違えがないかご確認ください"}
                      </span>
                      <span className="red">{emailEntered}</span>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
            <Grid
              container
              className="signup-group"
              spacing={2}
              alignItems="center"
            >
              <Grid item md={3} sm={3} xs={12}>
                <label>
                  パスワード <span className="required">必須</span>
                </label>
              </Grid>
              <Grid item md={9} sm={9} xs={12}>
                <div className="password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    maxLength={12}
                    id="password"
                    placeholder="パスワード"
                    className="input-field"
                    onChange={(e) => {
                      checkPasswordCriteria(e);
                      handleChange(e);
                    }}
                  />
                  <div className="password-btns">
                    <button
                      className={`password-btn show-password ${
                        showPassword ? "active" : ""
                      }`}
                      type="button"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      {showPassword ? <BiShowAlt /> : <BiHide />}
                      <div className="tooltip">パスワードを表示/非表示</div>
                    </button>
                    <button
                      onClick={copyPassword}
                      className="password-btn"
                      type="button"
                    >
                      <PiCopyDuotone />
                      <div className="tooltip">{copyText}</div>
                    </button>
                    <button
                      onClick={generatePassword}
                      className="password-btn"
                      type="button"
                    >
                      自動作成
                      <div className="tooltip">パスワードを自動で作成</div>
                    </button>
                  </div>
                  {/* <Grid
                    container
                    justifyContent="space-between"
                    className="space-1"
                    style={{ margin: "5px 0 10px" }}
                  >
                    <Grid item>
                      <button
                        className={`password-btn show-password ${
                          showPassword ? "active" : ""
                        }`}
                        type="button"
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                      >
                        {showPassword ? <BiHide /> : <BiShowAlt />}
                        パスワードを表示
                      </button>
                      
                      
                    </Grid>
                    <Grid item className="flex space-1">
                      <button
                        onClick={generatePassword}
                        className="password-btn"
                        type="button"
                      >
                        パスワードを自動で作成
                      </button>
                      <button
                        onClick={copyPassword}
                        className="password-btn"
                        type="button"
                      >
                        {copyText}
                      </button>
                    </Grid>
                  </Grid> */}
                </div>
                {passwordError && (
                  <Typography className="error-message">
                    {passwordError}
                  </Typography>
                )}
                {passwordRegexError && (
                  <div className="email-error">
                    <span>{"半角英数字で入力お願いします"}</span>
                  </div>
                )}
                <span style={{ fontSize: "0.8em" }}>
                  ※自動作成をクリック(タップ)するとパスワードが自動で生成されます。自動で生成されたパスワードは必ずお手元に控えてください。
                  <br />
                  ※目アイコンをクリック(タップ)すると入力したパスワードが表示されます
                </span>
                <span
                  className={`password-criteria ${
                    passwordMatch ? "active" : ""
                  }`}
                >
                  {passwordMatch ? <FaCheck /> : <IoMdClose />} 6桁〜12桁
                </span>
              </Grid>
            </Grid>
            {displayLp != "lp" && (
              <Grid
                container
                spacing={4}
                alignItems="flex-start"
                style={{ marginTop: "30px" }}
                className="signup-group"
              >
                <Grid item md={3} sm={3} xs={12}>
                  <label>お知りになったきっかけ</label>
                </Grid>
                <Grid item md={9} sm={9} xs={12} className="input-checkboxes">
                  <div>
                    <input
                      type="checkbox"
                      value="SNS(Tiktok、Instagramなど)"
                      name="survey"
                      id="SNS(Tiktok、Instagramなど)"
                    />
                    <label
                      className="input-label"
                      htmlFor="SNS(Tiktok、Instagramなど)"
                    >
                      SNS(Tiktok、Instagramなど)
                    </label>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      value="インターネット(Googleなど)"
                      name="survey"
                      id="インターネット(Googleなど)"
                    />
                    <label
                      className="input-label"
                      htmlFor="インターネット(Googleなど)"
                    >
                      インターネット(Googleなど)
                    </label>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      value="LINEの広告"
                      name="survey"
                      id="LINEの広告"
                    />
                    <label className="input-label" htmlFor="LINEの広告">
                      LINEの広告
                    </label>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      name="survey"
                      value="知り合いからの紹介"
                      id="知り合いからの紹介"
                    />
                    <label className="input-label" htmlFor="知り合いからの紹介">
                      知り合いからの紹介
                    </label>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      value="その他"
                      name="survey"
                      id="その他"
                    />
                    <label className="input-label" htmlFor="その他">
                      その他
                    </label>
                  </div>
                </Grid>
              </Grid>
            )}
            {displayLp != "lp" && (
              <Typography
                className="text-center"
                style={{ margin: "40px 0 30px" }}
              >
                <Link
                  to="https://jibun-it.jp/terms"
                  target="_blank"
                  style={{
                    color: "var(--text-color-3)",
                    borderBottom: "1px solid",
                  }}
                >
                  じぶん専用IT相談室 利用規約個人情報の取扱について
                </Link>
              </Typography>
            )}

            <Box className="text-center">
              <button
                type="submit"
                className="classification-btn"
                disabled={loading}
              >
                {loading ? <ButtonLoader /> : " 利用規約に同意して会員登録"}
              </button>
              {signUpError && (
                <Typography className="error-message">
                  ご入力いただいたメールアドレスは既に使用されています。
                </Typography>
              )}
              {displayLp != "lp" && (
                <Typography style={{ marginTop: "40px" }}>
                  すでにアカウントをお持ちの方は、
                  <Link
                    to="/login"
                    style={{
                      color: "#2683c3",
                      borderBottom: "1px solid",
                    }}
                  >
                    ログイン
                  </Link>
                  してください
                </Typography>
              )}
            </Box>
          </form>
        </Box>
      </Container>
    </Box>
  );
}

export default Signup;
